<template>
  <div class="collapsible-section" :class="{ collapsed: !isOpen }">
    <button
      class="collapsible-section__header"
      :class="{ 'is-static': !collapsible }"
      @click="toggle"
      :aria-expanded="isOpen"
    >
      <div class="collapsible-section__title">
        <slot name="icon">
          <i v-if="icon" :class="icon" class="collapsible-section__icon" />
        </slot>
        <span>{{ title }}</span>
        <span v-if="badge" class="collapsible-section__badge">{{ badge }}</span>
      </div>
      <i
        v-if="collapsible"
        class="tim-icons collapsible-section__chevron"
        :class="isOpen ? 'icon-minimal-up' : 'icon-minimal-down'"
      />
    </button>

    <transition name="collapse">
      <div v-show="isOpen" class="collapsible-section__content">
        <div class="collapsible-section__body">
          <p v-if="description" class="collapsible-section__description">
            {{ description }}
          </p>
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'CollapsibleSection',

  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    badge: {
      type: [String, Number],
      default: ''
    },
    defaultOpen: {
      type: Boolean,
      default: true
    },
    // When false, the section renders as a static card (no chevron, no toggle,
    // always open) — same accent/icon/title/badge chrome, used where the
    // collapse affordance isn't wanted.
    collapsible: {
      type: Boolean,
      default: true
    },
    value: {
      type: Boolean,
      default: undefined
    }
  },

  data() {
    return {
      internalOpen: this.defaultOpen
    }
  },

  computed: {
    isOpen() {
      if (!this.collapsible) {
        return true
      }
      if (this.value !== undefined) {
        return this.value
      }
      return this.internalOpen
    }
  },

  methods: {
    toggle() {
      if (!this.collapsible) {
        return
      }
      if (this.value !== undefined) {
        this.$emit('input', !this.value)
      } else {
        this.internalOpen = !this.internalOpen
      }
      this.$emit('toggle', !this.isOpen)
    },

    open() {
      if (this.value !== undefined) {
        this.$emit('input', true)
      } else {
        this.internalOpen = true
      }
    },

    close() {
      if (this.value !== undefined) {
        this.$emit('input', false)
      } else {
        this.internalOpen = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.collapsible-section {
  background: rgba(255, 255, 255, 0.03);
  border: none;
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  margin-bottom: 16px;
  overflow: visible;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &.collapsed {
    .collapsible-section__header {
      border-bottom-color: transparent;
      border-radius: 0.375rem;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 16px 20px;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    color: #fff;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:hover {
      background: rgba(255, 255, 255, 0.02);
    }

    &.is-static {
      cursor: default;
      &:hover { background: transparent; }
    }

    border-radius: 0.375rem 0.375rem 0 0;

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px rgba(225, 78, 202, 0.4);
    }
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    span {
      white-space: nowrap;
    }

    @media (max-width: 991px) {
      gap: 8px;
      font-size: 13px;
    }

    @media (max-width: 767px) {
      gap: 6px;
      font-size: 12px;
      letter-spacing: 0.3px;
    }

    @media (max-width: 575px) {
      font-size: 11px;
      letter-spacing: 0.2px;
    }
  }

  &__icon {
    font-size: 16px;
    color: #e14eca;
  }

  &__badge {
    flex-shrink: 0;
    padding: 2px 8px;
    background: rgba(225, 78, 202, 0.2);
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
    color: #e14eca;

    @media (max-width: 991px) {
      padding: 2px 7px;
      font-size: 10px;
    }

    @media (max-width: 767px) {
      padding: 1px 6px;
      font-size: 9.5px;
      border-radius: 8px;
    }

    @media (max-width: 575px) {
      padding: 1px 5px;
      font-size: 9px;
    }
  }

  &__chevron {
    flex-shrink: 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.5);
    transition: transform 0.2s;
  }

  &__content {
    overflow: hidden;
  }

  &__body {
    padding: 20px;

    // The lead element shouldn't add its own top margin on top of the body
    // padding (e.g. info-hint's built-in 12px), which reads as a dead gap under
    // the header.
    > :first-child {
      margin-top: 0;
    }
  }

  &__description {
    margin: 0 0 16px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
  }
}

// Collapse transition
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
}

.collapse-enter,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

</style>

<style lang="scss">
/* Light mode overrides - CollapsibleSection */
.white-content .collapsible-section {
  background: rgba(0, 0, 0, 0.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .collapsible-section__header {
    color: #344675;
    border-bottom-color: rgba(0, 0, 0, 0.08);

    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }
  }

  .collapsible-section__chevron {
    color: rgba(0, 0, 0, 0.4);
  }

  .collapsible-section__description {
    color: rgba(0, 0, 0, 0.5);
  }
}
</style>
